// Сборка библиотеки в полезную нагрузку для магии. Чистый модуль без React:
// вся работа со временем и таймзонами делается здесь, один раз, и проверяется
// тестами (payload.test.ts).
import type { AppState, Currency, CurrencyDef, Segment } from '../../types';
import { convertMinor, segmentTitle, toUtcMillis } from '../../core';
import { lookupTz } from '../../parser';
import type { Translate } from '../../i18n';
import type { MagicSegmentInput } from './api';

/**
 * Таймзона рейса: своя, если она у блока проставлена, иначе — из таблицы
 * аэропортов по коду. Пустая строка означает «неизвестна»: время такого рейса
 * в UTC не пересчитать, и сервер честно скажет, что пересечение не проверял.
 */
function tzFor(explicit: string, iata: string): string {
  return explicit || (iata ? lookupTz(iata) : '');
}

/** Валюта и курсы для пересчёта цен блоков в одну валюту. */
export interface MagicMoneyContext {
  to: Currency;
  currencies: readonly CurrencyDef[];
}

/**
 * Цена блока. С контекстом пересчёта — в валюте отображения: «самый дешёвый»
 * вариант модель выбирает сравнением чисел, а числа в разных валютах
 * несравнимы. Пересчёт — тот же convertMinor, что считает итоги на доске,
 * поэтому цена в каталоге модели и цена в карточке варианта сходятся.
 */
function priceOf(seg: Segment, money?: MagicMoneyContext): { priceMinor: number; currency: Currency } {
  const price = seg.kind === 'lodging' ? seg.pricePerNight : seg.price;
  if (!money) return { priceMinor: price.amount, currency: price.currency };
  return { priceMinor: convertMinor(price, money.to, money.currencies), currency: money.to };
}

/** Один блок библиотеки → строка каталога для модели. */
export function toMagicSegment(seg: Segment, t: Translate, money?: MagicMoneyContext): MagicSegmentInput {
  const base: MagicSegmentInput = {
    id: seg.id,
    kind: seg.kind,
    title: segmentTitle(seg, t),
    ...priceOf(seg, money),
  };

  switch (seg.kind) {
    case 'flight': {
      const fromTz = tzFor(seg.fromTz, seg.from);
      const toTz = tzFor(seg.toTz, seg.to);
      return {
        ...base,
        from: seg.from,
        to: seg.to,
        carrier: seg.carrier,
        flightNumber: seg.flightNumber ?? '',
        departLocal: seg.departLocal,
        arriveLocal: seg.arriveLocal,
        departUtc: toUtcMillis(seg.departLocal, fromTz),
        arriveUtc: toUtcMillis(seg.arriveLocal, toTz),
        priceScope: seg.priceScope,
        linkedTo: seg.linkedTo ?? '',
      };
    }
    case 'transfer':
      return { ...base, from: seg.from, to: seg.to, durationMin: seg.durationMin };
    case 'lodging':
      return { ...base, city: seg.city, nights: seg.nights };
    case 'baggage':
      return { ...base, flightId: seg.flightId, label: seg.label };
    default:
      return { ...base, label: seg.label, city: seg.city ?? '', quantity: seg.quantity };
  }
}

/**
 * Вся библиотека для запроса. Порядок — рейсы первыми и по времени вылета:
 * каталог модель читает сверху вниз, и хронология в нём помогает ей не
 * перепутать плечи маршрута.
 */
export function toMagicSegments(state: AppState, t: Translate): MagicSegmentInput[] {
  const money: MagicMoneyContext = { to: state.displayCurrency, currencies: state.currencies };
  const items = Object.values(state.library).map((seg) => toMagicSegment(seg, t, money));
  return items.sort((a, b) => {
    if (a.kind === 'flight' && b.kind !== 'flight') return -1;
    if (a.kind !== 'flight' && b.kind === 'flight') return 1;
    const at = a.departUtc ?? null;
    const bt = b.departUtc ?? null;
    if (at !== null && bt !== null && at !== bt) return at - bt;
    return 0;
  });
}
